import { isObject } from "./objects";

interface EqualityOptions {
  orderedArrays: boolean;
}
const DEFAULT_OPTIONS = { orderedArrays: true };

// we explicitly want to compare any function-like inputs
// eslint-disable-next-line @typescript-eslint/ban-types
const areFunctionsEqual = (a: Function, b: Function): boolean => {
  return a.toString() === b.toString();
};

const arePrimitivesEqual = (a: unknown, b: unknown): boolean => a === b;

const getType = (input: unknown): string => {
  return Object.prototype.toString.call(input).slice(8, -1).toLowerCase();
};

const areObjectsEqual = (
  a: Record<string, any>,
  b: Record<string, any>,
  opts: EqualityOptions = DEFAULT_OPTIONS
): boolean => {
  if (Object.keys(a).length !== Object.keys(b).length) {
    return false;
  }

  for (const key in a) {
    if (
      Object.prototype.hasOwnProperty.call(a, key) &&
      !isEqual(a[key], b[key], opts)
    ) {
      return false;
    }
  }

  return true;
};

export const areArraysEqual = <T extends any[]>(
  a: T | undefined,
  b: T | undefined,
  opts: EqualityOptions = DEFAULT_OPTIONS
): boolean => {
  if (a === b) {
    return true;
  }
  if (!a || !b) {
    return false;
  }
  if (a.length !== b.length) {
    return false;
  }
  return a.every((item, index) => isEqual(b[index], item, opts));
};

export const areArraysEqualUnordered = <T extends any[]>(
  a: T | undefined,
  b: T | undefined,
  opts: EqualityOptions = DEFAULT_OPTIONS
): boolean => {
  if (a === b) {
    return true;
  }
  if (!a || !b) {
    return false;
  }
  if (a.length !== b.length) {
    return false;
  }
  const bRemaining = [...b];
  for (const aItem of a) {
    const matchIndex = bRemaining.findIndex((bItem) =>
      isEqual(aItem, bItem, opts)
    );
    if (matchIndex === -1) {
      return false;
    }
    bRemaining.splice(matchIndex, 1);
  }
  return true;
};

export const isEqual = (
  a: unknown,
  b: unknown,
  opts: EqualityOptions = DEFAULT_OPTIONS
): boolean => {
  // Get the object type
  const type = getType(a);

  // If the two items are not the same type, return false
  if (type !== getType(b)) {
    return false;
  }

  // Compare based on type
  if (Array.isArray(a) && Array.isArray(b)) {
    return opts.orderedArrays
      ? areArraysEqual(a, b, opts)
      : areArraysEqualUnordered(a, b, opts);
  }
  if (isObject(a) && isObject(b)) {
    return areObjectsEqual(a, b, opts);
  }
  if (typeof a === "function" && typeof b === "function") {
    return areFunctionsEqual(a, b);
  }
  return arePrimitivesEqual(a, b);
};
